import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Loader2, Music, User } from "lucide-react";
import StatusBadge from "./StatusBadge";
import SkeletonCard from "./SkeletonCard";
import { useNavigate } from "react-router-dom";

const SongAvatar = ({ size = "large" }) => {
  const sizeClasses = {
    large: "w-5 h-5",
    small: "w-4 h-4",
  };

  return (
    <div
      className={`bg-gray-100 ${sizeClasses[size]} rounded-full flex items-center justify-center`}
    >
      <Music className={`text-neutral-700 ${size === "large" ? "h-3 w-3" : "h-2 w-2"}`} />
    </div>
  );
};

const ProfileAvatar = ({ name, role, size = "large" }) => {
  const bgColors = {
    admin: "bg-indigo-600",
    "super user": "bg-emerald-600",
  };

  const sizeClasses = {
    large: "w-5 h-5 text-xs",
    small: "w-4 h-4 text-[10px]",
  };

  const bgColor = bgColors[role] || "bg-zinc-800";
  const firstLetter = name ? name.charAt(0).toUpperCase() : "?";

  return (
    <div
      className={`${bgColor} ${sizeClasses[size]} rounded-full flex items-center justify-center text-white font-medium`}
    >
      {firstLetter}
    </div>
  );
};

const RecentlyEditedSongs = () => {
  const { t, i18n } = useTranslation();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecentEdits = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/song-edits/recent`, {
          params: { limit: 10 },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        });

        const formattedSongs = await Promise.all(
          response.data.map(async (edit) => {
            let title = edit.songTitle || t("recentlyEditedSongs.untitledSong");
            let user = edit.userId?.username || t("recentlyEditedSongs.unknownUser");
            let role = edit.userId?.role || edit.role || "super user";

            // Only fetch song title if songTitle is missing or invalid and songId is valid
            if ((!edit.songTitle || edit.songTitle === t("recentlyEditedSongs.untitledSong")) && edit.songId) {
              try {
                const songIdNum = parseInt(edit.songId, 10);
                if (!isNaN(songIdNum)) {
                  const songResponse = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongBySongID/${songIdNum}`,
                    {
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
                      },
                    }
                  );
                  console.log(`Fetched title for songId ${songIdNum}:`, songResponse.data.title);
                  title = songResponse.data.title || t("recentlyEditedSongs.untitledSong");
                } else {
                  console.warn(`Invalid songId: ${edit.songId}`);
                }
              } catch (songError) {
                console.error(`Error fetching song title for songId ${edit.songId}:`, songError.response?.data || songError.message);
                title = t("recentlyEditedSongs.untitledSong");
              }
            }

            if (!edit.userId) {
              user = t("recentlyEditedSongs.systemEdit");
              role = edit.role || "super user";
            }

            let formattedDate = t("recentlyEditedSongs.recently");
            if (edit.editedAt) {
              try {
                const date = new Date(edit.editedAt);
                if (!isNaN(date.getTime())) {
                  formattedDate = date.toLocaleDateString(i18n.language, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  });
                }
              } catch (dateError) {
                console.error("Error formatting date:", dateError);
              }
            }

            return {
              id: edit._id || `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              title,
              user,
              role,
              songId: edit.songId,
              editedAt: formattedDate,
            };
          })
        );

        setSongs(formattedSongs);
        setLoading(false);
      } catch (err) {
        setError(t("recentlyEditedSongs.error"));
        setLoading(false);
        console.error("Error fetching recent edits:", err);
      }
    };

    fetchRecentEdits();
  }, [t, i18n.language]);

  const formatRole = (role) => {
    return t(`recentlyEditedSongs.roles.${role.toLowerCase()}`, role.charAt(0).toUpperCase() + role.slice(1));
  };

  const isRTL = i18n.language === "ar";

  const showAll = songs.length > 6;
  const displayedSongs = showAll ? songs.slice(0, 6) : songs;

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[...Array(3)].map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="rounded-2xl border border-gray-100 shadow">
        <CardContent className="p-4 text-red-500 text-center text-sm">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-neutral-700 flex items-center gap-2">
          {t("recentlyEditedSongs.title")}
        </h2>
        {showAll && (
          <button
            className="px-4 py-1 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors shadow"
            onClick={() => navigate("/all-songs")}
          >
            {t("conferences.showMore")}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {displayedSongs.length === 0 ? (
          <Card className="rounded-2xl border border-gray-100 shadow">
            <CardContent className="py-8 text-center text-neutral-700 text-sm">
              {t("recentlyEditedSongs.noSongs")}
            </CardContent>
          </Card>
        ) : (
          displayedSongs.map((song) => (
            <Card
              key={song.id}
              className="rounded-2xl border border-gray-100 shadow hover:shadow-lg transition-shadow group cursor-pointer"
              onClick={() => song.songId && (window.location.href = `/edit-song/${song.songId}`)}
            >
              <CardContent className="p-4 flex flex-col gap-2">
                <div className="flex items-center gap-3 mb-1">
                  <div className="bg-zinc-100 rounded-full p-2">
                    <Music className="w-6 h-6 text-zinc-400" />
                  </div>
                  <div>
                    <div className="font-medium text-neutral-800 text-base truncate">
                      {song.title}
                    </div>
                    <div className="text-xs text-zinc-400 flex items-center gap-1">
                      <User className="w-4 h-4 text-zinc-400" />
                      {song.user}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge status={song.role}>
                    {formatRole(song.role)}
                  </StatusBadge>
                  <span className="text-xs text-zinc-400 ml-2">
                    {song.editedAt}
                  </span>
                </div>
                <button
                  className={`mt-3 self-end px-4 py-1 bg-zinc-800 text-white rounded-lg text-sm font-medium hover:bg-black transition-colors shadow ${!song.songId ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={e => { e.stopPropagation(); if (song.songId) window.location.href = `/edit-song/${song.songId}`; }}
                  disabled={!song.songId}
                  title={t(song.songId ? "recentlyEditedSongs.editButton" : "recentlyEditedSongs.editButtonDisabled")}
                >
                  {t("recentlyEditedSongs.editButton")}
                </button>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentlyEditedSongs;